<script lang="ts" setup>
import CommonAPI from '~/api/common';
import type { ResponseError } from '~/api/utils/types';
import abbreviationLogo from '~/assets/icons/logo/abbreviation.svg?url';
import nameLogo from '~/assets/icons/logo/name.svg?url';
import background from '~/assets/images/subscribe_page_background.png';

const TEST_ID = 'page-subscribe';

const { t } = useI18n();
const modalStore = useModalStore();
const dataLayer = useDataLayer();

const email = ref('');
const { execute, isLoading } = useRequest(CommonAPI.subscribe, () => ({
  body: { email: email.value },
}));

const onSubmit = async () => {
  execute()
    ?.then(() => {
      dataLayer.append({ event: 'email-subscribe' }, ['user']);
      modalStore.showSuccess({
        title: t('message.success'),
        action: t('message.return'),
      });
      email.value = '';
    })
    .catch((e: ResponseError) => {
      modalStore.showError({
        title: getFetchErrorMessage(e, t('message.error')),
        action: t('message.return'),
      });
    });
};
</script>

<template>
  <section class="pageSubscribe" :data-test-id="TEST_ID">
    <NuxtLink to="/">
      <picture>
        <source :srcset="nameLogo" media="(min-width: 1240px)" />
        <img
          :src="abbreviationLogo"
          alt="Don't Touch My Skin"
          class="pageSubscribe__logo"
        />
      </picture>
    </NuxtLink>

    <NuxtImg
      :sizes="IMAGE_SIZES.fullscreen"
      format="webp"
      :src="background"
      class="pageSubscribe__image"
      :alt="t('image_alt')"
    />

    <div class="pageSubscribe__wrapper">
      <div class="pageSubscribe__content">
        <i18n-t tag="h1" keypath="title" class="pageSubscribe__title">
          <span>{{ t('discount') }}</span>
        </i18n-t>
        <p class="pageSubscribe__description">{{ t('text') }}</p>

        <form @submit.prevent="onSubmit" class="pageSubscribe__form">
          <label class="pageSubscribe__field">
            <input
              v-model="email"
              autocomplete="email"
              inputmode="email"
              name="email"
              placeholder="email@example.ru"
              class="pageSubscribe__input"
              :data-test-id="`${TEST_ID}-email-input`"
            />
            <span class="pageSubscribe__hint">
              {{ t('hint') }}
              <NuxtLink to="/personal">
                {{ t('hint_link') }}
              </NuxtLink>
            </span>
          </label>

          <BaseButton
            :loading="isLoading"
            mobile-size="xs"
            dark
            block
            inverted-color="#8583ba"
            type="submit"
            size="s"
            :data-test-id="`${TEST_ID}-submit`"
          >
            {{ t('action') }}
          </BaseButton>
        </form>
      </div>
    </div>
  </section>
</template>

<i18n>
ru:
  title: Скидка {0} для вдохновения
  discount: 15% –
  text: Подпишись на нашу рассылку, чтобы знать больше, пробовать первым и превращать уход за собой в искусство
  hint: Нажимая кнопку “Подписаться”, вы соглашаетесь на
  hint_link: обработку персональных данных
  image_alt: Подпишитесь на рассылку от Don't Touch My Skin
  action: Подписаться
  message:
    success: Вы успешно подписались на рассылку
    error: Произошла ошибка, повторите попытку позже
    return: Вернуться

en:
  title: '{0} Discount for Inspiration'
  discount: 15% –
  text: Subscribe to our newsletter to learn more, be the first to try, and turn self-care into an art form
  hint: By clicking the “Subscribe” button, you agree to the terms of use of the site
  image_alt: Subscribe to the Don't Touch My Skin newsletter
  action: Subscribe
  message:
    success: You have successfully subscribed to the newsletter
    error: An error occurred, please try again later
    return: Return
</i18n>

<style lang="scss">
.pageSubscribe {
  background-color: #f1f1f1;
  padding: 0 50px;
  min-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @include mq('md') {
    padding: 0 20px;
  }

  &__wrapper {
    margin: 0 auto;
    display: flex;
    position: relative;
    max-width: 1540px;
    width: 100%;
    max-height: 600px;
    flex: 1;
    align-items: flex-end;

    @include mq('md') {
      padding-top: 80px;
      align-items: flex-start;
    }

    @include mq('sm') {
      min-height: 700px;
    }
  }

  &__logo {
    width: 161px;
    filter: invert(1);
    padding-top: 50px;

    @include mq('lg') {
      width: 188px;
      filter: initial;
    }

    @include mq('sm') {
      width: 121px;
    }
  }

  &__image {
    object-fit: contain;
    display: block;
    position: absolute;
    height: 128%;
    max-width: 65%;
    right: 5%;
    top: -27%;

    @include mq('lg') {
      right: -12%;
      max-width: 80%;
    }

    @include mq('md') {
      top: auto;
      bottom: -3.5%;
      right: -20%;
      height: auto;
      max-width: 750px;
      width: 109%;
    }

    @include mq('sm') {
      right: -27.5%;
    }
  }

  &__content {
    position: relative;
    color: #000000;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: 437px;

    @include mq('md') {
      max-width: 307px;
    }
  }

  &__title {
    font-size: 41px;
    line-height: 44px;
    letter-spacing: 0.2px;
    margin: 0;
    width: 610px;
    max-width: 100%;
    white-space: pre-wrap;
    text-transform: uppercase;
    font-variation-settings: 'wght' 700;

    > span {
      font-variation-settings: inherit;
      color: #8583ba;
    }

    @include mq('md') {
      letter-spacing: 1px;
      font-size: 23px;
      line-height: 25px;
    }
  }

  &__description {
    font-size: 22px;
    line-height: 26px;
    letter-spacing: 0.2px;
    margin: 35px 0 80px;

    @include mq('md') {
      max-width: 248px;
      font-size: 15px;
      line-height: 18px;
      margin: 24px 0 42px;
    }
  }

  &__form {
    max-width: 327px;

    @include mq('md') {
      max-width: 248px;
    }
  }

  &__field {
    padding-left: 2px;
    margin-top: 80px;
  }

  &__input {
    width: 583px;
    display: block;
    border: 0;
    border-bottom: 1px solid #000;
    background: none;
    font-size: 17px;
    line-height: 26px;
    outline: none;
    padding: 7px 0;
    max-width: 100%;

    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }

    @include mq('sm') {
      font-size: 15px;
      line-height: 18px;
    }
  }

  &__hint {
    font-size: 13px;
    line-height: 20px;
    letter-spacing: 0.2px;
    display: block;
    margin-top: 15px;
    margin-bottom: 25px;
    font-variation-settings: 'ital' 900;

    a {
      color: #fff;
      border-bottom: 1px solid #ffffff;

      &:hover {
        border: none;
      }
    }

    @include mq('sm') {
      font-size: 11px;
      line-height: 16px;
      width: 275px;
      max-width: 172px;
      margin-top: 12px;
      margin-bottom: 29px;
    }
  }
}
</style>
